import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Mail, Phone, MapPin, Heart } from 'lucide-react';
import { SITE_CONFIG, ROUTES, IMAGES } from '../utils/constants';

const Footer = () => {
  return (
    <footer className="footer-section">
      <div className="container">
        <div className="footer-grid">
          {/* Brand Column */}
          <div className="footer-col">
            <div className="footer-logo">
              <img src={IMAGES.logo} alt="Little Star Logo" className="footer-logo-img" />
              <span className="ml-2"><span className="text-primary">Little Star</span> Academy</span>
            </div>
            <p className="footer-desc">
              Nurturing young minds with love, care, and the best educational practices. Join us in building a bright future for your child.
            </p>
            <div className="social-links">
              <a href={SITE_CONFIG.socials.facebook} className="social-link"><Facebook size={20} /></a>
              <a href={SITE_CONFIG.socials.twitter} className="social-link"><Twitter size={20} /></a>
              <a href={SITE_CONFIG.socials.instagram} className="social-link"><Instagram size={20} /></a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-col">
            <h3 className="footer-heading">Quick Links</h3>
            <ul className="footer-links">
              {ROUTES.map(route => (
                <li key={route.path}><Link to={route.path}>{route.name}</Link></li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="footer-col">
            <h3 className="footer-heading">Contact Us</h3>
            <ul className="contact-list">
              <li>
                <Phone size={18} className="contact-icon" />
                <span>{SITE_CONFIG.phone}</span>
              </li>
              <li>
                <Mail size={18} className="contact-icon" />
                <span>{SITE_CONFIG.email}</span>
              </li>
              <li>
                <MapPin size={18} className="contact-icon" />
                <span>{SITE_CONFIG.address}</span>
              </li>
            </ul>
          </div>

          {/* Map Column */}
          <div className="footer-col">
            <h3 className="footer-heading">Find Us</h3>
            <div className="footer-map-wrapper">
              <iframe
                src={SITE_CONFIG.mapLink}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Footer Google Map"
              ></iframe>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} {SITE_CONFIG.name}. All rights reserved.</p>
          <p className="made-with">
            Made with <Heart size={16} fill="var(--color-accent)" stroke="none" /> for kids
          </p>
        </div>
      </div>

      <style>{`
        .footer-section {
          background-color: var(--color-bg-white);
          padding-top: 5rem;
          padding-bottom: 2rem;
          border-top: 1px solid #e2e8f0;
          color: var(--color-text-light);
        }

        .footer-grid {
          display: grid;
          grid-template-columns: 1.5fr 0.8fr 1fr 1.5fr;
          gap: 2rem;
          margin-bottom: 4rem;
        }

        .footer-logo {
          font-family: var(--font-heading);
          font-size: 1.75rem;
          font-weight: 800;
          color: var(--color-text-main);
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
        }

        .footer-logo-img {
          height: 55px; /* Increased from 40px */
          width: auto;
        }
        
        .ml-2 { margin-left: 0.75rem; }

        .footer-desc {
          margin-bottom: 2rem;
          max-width: 350px;
        }

        .social-links {
          display: flex;
          gap: 1rem;
        }

        .social-link {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: var(--color-bg-light);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-primary);
          transition: all 0.2s;
        }

        .social-link:hover {
          background-color: var(--color-primary);
          color: white;
          transform: translateY(-3px);
        }

        .footer-heading {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--color-text-main);
          margin-bottom: 1.5rem;
        }

        .footer-links li {
          margin-bottom: 0.75rem;
        }

        .footer-links a:hover {
          color: var(--color-primary);
          padding-left: 5px;
        }

        .contact-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .contact-icon {
          color: var(--color-secondary-dark);
          flex-shrink: 0;
          margin-top: 3px;
        }
        
        .footer-map-wrapper {
            height: 200px;
            border-radius: 1rem;
            overflow: hidden;
            box-shadow: var(--shadow-sm);
            background: #f1f5f9;
        }

        .footer-bottom {
          border-top: 1px solid #e2e8f0;
          padding-top: 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.875rem;
        }

        .made-with {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        @media (max-width: 1024px) {
            .footer-grid {
                grid-template-columns: 1fr 1fr;
                gap: 3rem;
            }
        }

        @media (max-width: 640px) {
          .footer-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }
          
          .footer-bottom {
            flex-direction: column;
            gap: 1rem;
            text-align: center;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;